import { i18n } from "@lingui/core";
import { clickable } from "@warp-ds/css/component-classes";
import WarpElement from "@warp-ds/elements-core";
import { css, html, TemplateResult } from "lit";
import { trackClickEvent } from "../tracking.js";
import { Brand, Subvertical } from "../../constants.js";
import { TrackingEventMap } from "../../models/trackingEvent.js";
import { activateI18n } from "../../utils/i18n.js";
import { SHARED_TOOLS_CONFIG } from "../../shared/tools-config.js";

interface CardWithIcon {
    icon: TemplateResult<1>;
    href: string;
    title: string;
    textStyle?: string;
    description: string;
    track?: (destination: string) => void;
}

interface Button {
    title: string;
    href: string;
    track: (destination: string) => void;
}

type Card = CardWithIcon;

class UsefulTools extends WarpElement {
    declare baseurl: string;
    declare subVertical: Subvertical;
    declare trackingEventMap: TrackingEventMap;
    declare brand: string;
    declare blocketIconUrl: string;

    static properties = {
        baseurl: { attribute: true, type: String },
        subVertical: { attribute: true, type: Subvertical },
        trackingEventMap: { type: Object },
        brand: { attribute: true, type: String },
        blocketIconUrl: { attribute: true, type: String },
    };

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-1zhaWb;

            .description-container {
                grid-template-rows: subgrid;
                grid-row: span 3;
            }
        `,
    ];

    // User-defined type guard function for CardWithIcon
    isCardWithIcon(card: Card): card is CardWithIcon {
        return "icon" in card;
    }

    cardWithIcon(card: CardWithIcon, isExtended = false) {
        return html`
            <w-card class="grid">
                ${isExtended
                    ? html`
                          <!-- Mobile layout: compact -->
                          <div class="p-16 grid gap-24 relative description-container md:hidden">
                              ${card.icon}
                              <div>
                                  <h4 class="t4 flex">
                                      <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                          <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                          ${card.title}
                                      </a>
                                      <w-icon name="ChevronRight" size="16px" class="flex items-center" locale="nb"></w-icon>
                                  </h4>
                                  <p class="text-body">${card.description}</p>
                              </div>
                          </div>
                          <!-- Desktop layout: extended -->
                          <div class="p-16 relative hidden md:block">
                              <div class="flex gap-24 items-center">
                                  ${card.icon}
                                  <div class="flex-1">
                                      <h4 class="t4 flex mb-8">
                                          <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                              <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                              ${card.title}
                                          </a>
                                      </h4>
                                      <p class="text-body">${card.description}</p>
                                  </div>
                                  <div class="items-center self-center">
                                      <w-icon name="ChevronRight" size="16px" locale="sv"></w-icon>
                                  </div>
                              </div>
                          </div>
                      `
                    : html`
                          <div class="p-16 grid gap-24 relative description-container">
                              ${card.icon}
                              <div>
                                  <h4 class="t4 flex">
                                      <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                          <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                          ${card.title}
                                      </a>
                                      <w-icon name="ChevronRight" size="16px" class="flex items-center" locale="sv"></w-icon>
                                  </h4>
                                  <p class="text-body">${card.description}</p>
                              </div>
                          </div>
                      `}
            </w-card>
        `;
    }

    renderCards(cards: Card[]) {
        const isExtended = cards.length === 1;
        const gridCols = isExtended ? "md:grid-cols-1" : "md:grid-cols-3";
        return html`
            <div class="flex flex-col md:grid ${gridCols} gap-16">
                ${cards.map((card: Card) => {
                    if (this.isCardWithIcon(card)) return this.cardWithIcon(card, isExtended);
                    return "";
                })}
            </div>
        `;
    }

    renderButton(button: Button) {
        return html`
            <w-card>
                <a
                    href="${button.href}"
                    aria-label="${button.title}"
                    class="${clickable.buttonOrLinkStretch}"
                    @click="${() => button.track(button.href)}"
                ></a>
                <w-button variant="link" class="grid s-bg place-items-center p-16">${button.title}</w-button>
            </w-card>
        `;
    }

    renderButtons(buttons: Button[]) {
        const cols = buttons.length;
        return html`
            <div class="flex flex-col md:grid md:grid-cols-${cols} gap-16 mb-56">${buttons.map((button) => this.renderButton(button))}</div>
        `;
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }
    render() {
        const title = i18n.t({
            id: "market-frontpage.useful-tools",
            message: "Useful tools",
        });

        const brandConfig = SHARED_TOOLS_CONFIG()[this.brand as Brand];
        const tools = brandConfig?.[this.subVertical] || [];

        const cards: Card[] = tools.map((tool) => {
            const iconHtml =
                tool.icon === "BlocketIcon"
                    ? html`<img src="${this.blocketIconUrl}" alt="Blocket" />`
                    : html`<w-icon
                          name="${tool.icon}"
                          size="32px"
                          class="s-icon flex"
                          locale="${this.brand === Brand.DBA ? "da" : this.brand === Brand.BLOCKET ? "sv" : "nb"}"
                      ></w-icon>`;

            const isPrimaryBackgroundBroken = tool.iconBackground === "s-bg-primary-subtle";
            const backgroundStyle = isPrimaryBackgroundBroken ? "background-color: #E3F2FD;" : "";

            return {
                icon: html`<div
                    class="box-border h-[100] w-[100] rounded-full grid place-items-center ${tool.iconBackground}"
                    style="${backgroundStyle}"
                >
                    ${iconHtml}
                </div>`,
                href: tool.route,
                title: tool.header,
                description: tool.body ? tool.body : "",
                track: (destination: string) => trackClickEvent(this.trackingEventMap[tool.trackingNumber], destination),
            };
        });

        return html`
            <h3 class="mb-16">${title}</h3>
            ${this.renderCards(cards)}
        `;
    }
}

customElements.define("useful-tools", UsefulTools as unknown as CustomElementConstructor);
